// src/components/Einstellungen.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../UserContext';
import './Einstellungen.css';

const Einstellungen = () => {
  const { user: currentUser } = useUser();
  const [isEditMode, setIsEditMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Standard-Reihenfolge der Navigation
  const defaultNavOrder = [
    { id: 'dashboard', path: '/', icon: 'fas fa-home', label: 'Dashboard', module: 'dashboard' },
    { id: 'dienstuebergabe', path: '/dienstuebergabe', icon: 'fas fa-exchange-alt', label: 'Dienstübergabe', module: 'dienstuebergabe' },
    { id: 'staerkemeldung', path: '/staerkemeldung', icon: 'fas fa-users', label: 'Stärkemeldung', module: 'staerkemeldung' },
    { id: 'aufgaben', path: '/aufgaben', icon: 'fas fa-tasks', label: 'Aufgaben', module: 'aufgaben' },
    { id: 'feuerloescher', path: '/feuerloescher', icon: 'fas fa-fire-extinguisher', label: 'Feuerlöscher', module: 'feuerloescher' },
    { id: 'engeRaeume', path: '/enge-raeume', icon: 'fas fa-door-closed', label: 'Enge Räume', module: 'engeRaeume' },
    { id: 'berichte', path: '/berichte', icon: 'fas fa-file-alt', label: 'Berichte', module: 'berichte' },
    { id: 'dienstplan', path: '/dienstplan', icon: 'fas fa-calendar-alt', label: 'Dienstplan', module: 'dienstplan' },
    { id: 'freiwuenscheUrlaub', path: '/freiwuensche-urlaub', icon: 'fas fa-calendar-check', label: 'Freiwünsche & Urlaub', module: 'freiwuenscheUrlaub' },
    { id: 'passwort', path: '/passwort', icon: 'fas fa-key', label: 'Passwort', module: 'passwort' },
    { id: 'speicherorte', path: '/speicherorte', icon: 'fas fa-save', label: 'Speicherorte', module: 'speicherorte' },
    { id: 'einstellungen', path: '/einstellungen', icon: 'fas fa-cog', label: 'Einstellungen', module: 'einstellungen' },
    { id: 'benutzerverwaltung', path: '/benutzerverwaltung', icon: 'fas fa-users-cog', label: 'Benutzerverwaltung', module: 'benutzerverwaltung', adminOnly: true },
    { id: 'modulfunktionen', path: '/modulfunktionen', icon: 'fas fa-cogs', label: 'Modulfunktionen', module: 'modulfunktionen', adminOnly: true }
  ];

  // Benutzerdefinierte Reihenfolge aus localStorage laden
  const [navOrder, setNavOrder] = useState(() => {
    const saved = localStorage.getItem('sidebarNavOrder');
    return saved ? JSON.parse(saved) : defaultNavOrder;
  });

  // Prüft, ob der Benutzer Admin-Rechte hat
  const hasAdminRights = () => {
    return currentUser && (currentUser.role === "admin" || currentUser.role === "superadmin");
  };

  // Drag & Drop Funktionen
  const handleDragStart = (e, index) => {
    if (!isEditMode) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    if (!isEditMode) return;
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    if (!isEditMode || dragIndex === null) return;
    e.preventDefault();
    
    const newOrder = [...navOrder];
    const draggedItem = newOrder[dragIndex];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setNavOrder(newOrder);
    localStorage.setItem('sidebarNavOrder', JSON.stringify(newOrder));
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Edit-Modus umschalten
  const toggleEditMode = () => {
    if (isEditMode) {
      // Beim Beenden des Edit-Modus Reihenfolge speichern
      localStorage.setItem('sidebarNavOrder', JSON.stringify(navOrder));
      // storage-Event manuell auslösen, damit Sidebar im gleichen Tab sofort reagiert
      window.dispatchEvent(new StorageEvent('storage', { key: 'sidebarNavOrder', newValue: JSON.stringify(navOrder) }));
    }
    setIsEditMode(!isEditMode);
  };

  // Reihenfolge zurücksetzen
  const resetOrder = () => {
    setNavOrder(defaultNavOrder);
    localStorage.setItem('sidebarNavOrder', JSON.stringify(defaultNavOrder));
  };

  // Prüft, ob ein Element angezeigt werden soll
  const shouldShowItem = (item) => {
    if (item.adminOnly && !hasAdminRights()) return false;
    return true;
  };

  return (
    <div className="einstellungen-container">
      <div className="einstellungen-header">
        <h2><i className="fas fa-cog"></i> Einstellungen</h2>
      </div>

      <div className="einstellungen-section">
        <h3><i className="fas fa-list"></i> Sidebar-Navigation</h3>
        <p className="section-description">
          Hier können Sie die Reihenfolge der Menüpunkte in der Sidebar anpassen. 
          Ziehen Sie die Elemente per Drag & Drop an die gewünschte Position.
        </p>

        {hasAdminRights() ? (
          <div className="edit-controls">
            <button 
              className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
              onClick={toggleEditMode}
            >
              <i className={`fas ${isEditMode ? 'fa-check' : 'fa-edit'}`}></i>
              {isEditMode ? 'Bearbeitung beenden' : 'Reihenfolge bearbeiten'}
            </button>
            {isEditMode && (
              <button 
                className="reset-order-btn"
                onClick={resetOrder}
                title="Standard-Reihenfolge wiederherstellen"
              >
                <i className="fas fa-undo"></i> Zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <div className="no-permission">
            <i className="fas fa-lock"></i>
            <p>Nur Administratoren können die Sidebar-Reihenfolge ändern.</p>
          </div>
        )}

        <div className="nav-order-list">
          {navOrder.filter(shouldShowItem).map((item, index) => {
            const isDragging = dragIndex === index;
            const isDragOver = dragOverIndex === index;

            return (
              <div
                key={item.id}
                className={`nav-item-card ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="nav-item-content">
                  <i className={item.icon}></i>
                  <span className="nav-item-label">{item.label}</span>
                  {isEditMode && (
                    <div className="drag-handle">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                  )}
                </div>
                {item.adminOnly && (
                  <div className="admin-badge">
                    <i className="fas fa-shield-alt"></i> Admin
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="einstellungen-section">
        <h3><i className="fas fa-link"></i> Weitere Einstellungen</h3>
        <div className="settings-links">
          <Link to="/speicherorte" className="settings-link">
            <i className="fas fa-save"></i>
            <span>Speicherorte</span>
          </Link>
          <Link to="/benutzerverwaltung" className="settings-link">
            <i className="fas fa-users-cog"></i>
            <span>Benutzerverwaltung</span>
          </Link>
        </div>
      </div>

    </div>
  );
};

export default Einstellungen;
